import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useParams, useNavigate } from "react-router-dom";
import axiosOrg, { AxiosResponse } from "axios";
import styled from "styled-components";

//custom components
import PdfViewer from "./PdfViewer.component";
import Breadcrumb from "../../../components/common/Breadcrumb/Breadcrumb.component";
import PageError from "../../../components/common/PageError/pageError.component";

//utils
import axios from "../../../utils/api";

//styles
import { linkColor, bgHeaders } from "../../../styles/theme";

type Path = {
  id: number;
  name: string;
};

export type MediaFile = {
  category_id: number;
  description: string;
  id: number;
  name: string;
  order: number;
  path: {
    extension: string;
    path: string;
  };
  published_at: string;
  type: string;
  breadcrumb: Path[];
};

interface Props {
  //   file: MediaFile | null;
  //   closeFn: () => void;
  //   groupTitle: string;
}

const HtmlInnerContent = styled.div`
  width: 90%;

  input {
    display: none;
  }
  .tabs {
    overflow: hidden;
    border-top: 1px solid #c4c4c4;
  }

  .tab {
    width: 100%;
    color: white;
    overflow: hidden;
    border-bottom: 1px solid #c4c4c4;
    display: flex;
    flex-direction: column;
  }
  .tab-label {
    display: flex;
    justify-content: space-between;
    background: ${bgHeaders};
    color: #4877ce;
    font-size: 15px;
    cursor: pointer;
    transition: all 0.2s linear;
    padding: 13px 15px;
    min-height: 20px;
    position: relative;
  }
  .tab-label:hover {
    background: #bad0ff;
  }
  .tab-label::after {
    content: "";
    width: 10px;
    height: 10px;
    text-align: center;
    transition: all 0.2s linear;
    transform: rotate(45deg);
    transform-origin: center center;
    display: block;
    position: absolute;
    border-right: 1px solid #4877ce;
    border-bottom: 1px solid #4877ce;
    right: 10px;
    top: 17px;
  }
  .tab-content {
    max-height: 0;
    padding: 0 25px;
    color: #2c3e50;
    background: white;
    transition: all 0.5s linear;
    overflow: hidden;
    margin-bottom: 0;
  }
  .tab-close {
    display: flex;
    justify-content: flex-end;
    padding: 15px;
    font-size: 15px;
    background: #2c3e50;
    cursor: pointer;
  }
  .tab-close:hover {
    background: #1a252f;
  }

  input:checked + .tab-label:hover {
    background: ${bgHeaders};
  }
  input:checked + .tab-label::after {
    transform: rotate(-135deg);
  }
  input:checked ~ .tab-content {
    max-height: 5000px;
    padding: 0 25px;
    margin-bottom: 15px;
  }
`;

const HtmlWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
`;

const FilePageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  h1 {
    font-size: 28px;
    line-height: 38px;
    color: ${linkColor};
    text-align: center;
    margin: 0 0 20px 0;
  }

  .file-content {
    img {
      max-width: 100%;
    }
    .no-file {
      width: 100%;
      text-align: center;
      display: inline-block;
    }
  }
`;

const FilePreview: React.FC<Props> = () => {
  const [fileData, setFileData] = useState<MediaFile | null>(null);
  const [htmlValue, setHtmlValue] = useState<string>("");
  const [errorVisible, setErrorVisible] = useState<boolean>(false);

  const { t, i18n } = useTranslation();
  const { fileId } = useParams();
  const navigate = useNavigate();

  //get entity data
  const { refetch: getFileData } = useQuery(
    "getFileData",
    async () => {
      // setVisible(false);
      const data = await axios(`folders/${fileId}?include.image`, {
        headers: {
          "Accept-Language": i18n.language,
        },
      });
      return data;
    },
    {
      onSuccess: (data: AxiosResponse<any>) => {
        setErrorVisible(false);
        if (!axiosOrg.isAxiosError(data)) {
          setFileData(data?.data?.data);
        }
      },
      onError: () => setErrorVisible(true),
    }
  );

  //get html data
  const { refetch: getHtmlData } = useQuery(
    "getHtmlData",
    async () => {
      // setVisible(false);
      const data = await axiosOrg(`${fileData?.path?.path}-download`, {
        headers: {
          "Accept-Language": i18n.language,
        },
      });
      return data;
    },
    {
      onSuccess: (data: AxiosResponse<any>) => {
        if (!axiosOrg.isAxiosError(data)) {
          setErrorVisible(false);
          setHtmlValue(data?.data || "");
        }
      },
      onError: () => setErrorVisible(true),
    }
  );

  useEffect(() => {
    getFileData();
  }, [i18n.language]);

  useEffect(() => {
    if (fileData?.path?.path) {
      getHtmlData();
    }
  }, [fileData]);

  return errorVisible ? (
    <PageError
      inPage
      externalRetryFn={getFileData}
      externalCancelFn={() => navigate(-1)}
    />
  ) : (
    <FilePageWrapper>
      {fileData && <Breadcrumb data={fileData.breadcrumb} />}
      <h1>{fileData?.name}</h1>
      <div className="file-content">
        {fileData?.path?.extension === "pdf" && <PdfViewer file={`${fileData?.path?.path}`} />}
        {["jpg", "jpeg", "png", "gif", "webp"].includes(`${fileData?.path?.extension}`) && (
          <img
            alt={`img-${fileData?.path?.extension}`}
            src={fileData?.path?.path}
          />
        )}
        {!["pdf", "html", "jpg", "jpeg", "png", "gif", "webp"].includes(
          `${fileData?.path?.extension}`
        ) && <span className="no-file">{t("common.fileCantPreview")}</span>}
        {fileData?.path?.extension === "html" && htmlValue && (
          <HtmlWrapper>
            <HtmlInnerContent dangerouslySetInnerHTML={{ __html: htmlValue }}></HtmlInnerContent>
          </HtmlWrapper>
        )}
      </div>
    </FilePageWrapper>
  );
};

export default FilePreview;
